import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CheckIcon from '@mui/icons-material/Check';
import { MenuItem } from '@mui/material';
import MediaTypography from 'components/MediaTypography';
import useAnchorEl from 'hooks/useAnchorEl';
import { Settings } from 'luxon';
import store from 'store';

import { useServiceRouter } from '@common/components/ServiceRouter';
import { getLanguageForService, getServiceLanguages } from '@common/helpers/serviceLanguages';

import SelectArrow from '../components/SelectArrow';
import { StyledLangMenu, StyledLangMenuWrap } from './styled';

type OptionType = {
	value: string;
	label: string;
};

function LangMenu() {
	const { anchorEl, open, handleClose, handleOpen } = useAnchorEl();
	const { checkService } = useServiceRouter();
	const currentServiceId = store.services.current?.id;
	const isLearningCenter = checkService('learningCenter');
	const { i18n } = useTranslation();
	const profileLang = store.profile?.lang?.toLocaleLowerCase();
	const options: OptionType[] = getServiceLanguages(currentServiceId).map((value) => ({
		value,
		label: value.toLocaleUpperCase(),
	}));

	useEffect(() => {
		if (!profileLang || !currentServiceId) return;

		const language = getLanguageForService(profileLang, currentServiceId);
		if (i18n.language !== language) {
			i18n.changeLanguage(language);
			Settings.defaultLocale = language;
			language !== profileLang && store.profile.changeUserLanguage(language);
		}
	}, [currentServiceId, profileLang, i18n]);

	if (isLearningCenter) {
		return (
			<MediaTypography
				variant="subtitle1"
				textColor="helper"
				sx={() => ({
					padding: '8px',
					order: '-1',
				})}
			>
				{i18n.resolvedLanguage}
			</MediaTypography>
		);
	}

	const selectedOption = options.find((option) => option.value === i18n.language);

	const handleSelect = (option: OptionType) => {
		i18n.changeLanguage(option.value.toLocaleLowerCase());
		store.profile.changeUserLanguage(option.value);
		Settings.defaultLocale = option.value;
		handleClose();
	};

	return (
		<>
			{/* @ts-ignore */}
			<StyledLangMenuWrap isOpen={open} onClick={handleOpen}>
				<MediaTypography variant="subtitle1" textColor="helper">
					{i18n.resolvedLanguage}
				</MediaTypography>
				<SelectArrow isOpen={open} />
			</StyledLangMenuWrap>
			<StyledLangMenu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				slotProps={{ paper: { sx: { width: anchorEl && anchorEl.offsetWidth } } }}
			>
				{options.map((option) => (
					<MenuItem
						onClick={() => handleSelect(option)}
						selected={selectedOption === option}
						key={option.value}
						sx={{ padding: '8px 4px 8px 4px', height: 'auto' }}
					>
						{option.label}
						{selectedOption === option && (
							<CheckIcon sx={{ fontSize: '16px', marginLeft: 'auto' }} />
						)}
					</MenuItem>
				))}
			</StyledLangMenu>
		</>
	);
}

export default observer(LangMenu);
